/** @module @liquidefy/code-editor/circuits/intelligent-terminal @version 1.0.0 @license LQD Research LLC */
/** @rust intelligent_terminal.rs — false; 4/2/26; sm */

/**
 * Tier 4 — Better Than Existing: Terminal That Understands Your Project
 *
 * Combines terminal state, session management, theming, guided hints,
 * command preview, command palette, quick actions, output digestion,
 * onboarding, and AI integrations into a terminal that knows your project.
 *
 * Pure circuit. No DOM, no framework coupling.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  TerminalState,
  ShellType,
  EditorResult,
} from '../code-editor-types.js';

import { ok, err, generateId, now, emptyTerminalState, asTerminalId } from '../code-editor.js';
import { createTerminal } from '../terminal/terminal.js';
import { createSession } from '../terminal/core/session.js';
import { createTerminalTheme } from '../terminal/appearance/theme.js';
import type { CommandHint, CommandExplanation } from '../terminal/guided/hints.js';
import { createHintState, generatePreExecutionHint, generatePostErrorHint, explainCommand } from '../terminal/guided/hints.js';
import type { CommandPreview } from '../terminal/guided/preview.js';
import { createPreviewState, generatePreview } from '../terminal/guided/preview.js';
import type { NLTranslation } from '../terminal/guided/palette.js';
import { createPaletteState, translateNaturalLanguage, fuzzyMatch } from '../terminal/guided/palette.js';
import type { QuickAction } from '../terminal/welcome/actions.js';
import { createActionState, getAvailableActions } from '../terminal/welcome/actions.js';
import type { OutputDigest } from '../terminal/welcome/digest.js';
import { createDigestState, digestOutput } from '../terminal/welcome/digest.js';
import type { SkillLevel, SkillAssessment } from '../terminal/welcome/onboarding.js';
import { createOnboardingState, assessSkillLevel, getSkillLevel } from '../terminal/welcome/onboarding.js';
import { generateSuggestions } from '../terminal/integrations/ai.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface IntelligentTerminalConfig {
  readonly shell?: ShellType;
  readonly colorScheme?: 'dark' | 'light';
  readonly projectType?: string;
  readonly projectFiles?: string[];
}

export interface ProjectContext {
  readonly type: string;
  readonly files: string[];
  readonly detectedFrameworks: string[];
  readonly detectedLanguages: string[];
  readonly hasPackageJson: boolean;
  readonly hasCargoToml: boolean;
  readonly hasGoMod: boolean;
  readonly hasMakefile: boolean;
  readonly hasDockerfile: boolean;
  readonly hasGit: boolean;
}

export interface IntelligentTerminalHandle extends CircuitHandle {
  getState(): TerminalState;
  suggestForContext(): string[];
  previewCommand(command: string): CommandPreview;
  digestOutput(command: string, output: string, exitCode: number): OutputDigest;
  explainError(command: string, error: string): { explanation: string; suggestion: string };
  getProjectActions(): QuickAction[];
  translateNL(input: string): { command: string; confidence: number };
  getHint(command: string): CommandHint | null;
  recordCommand(command: string): void;
  getCommandHistory(): string[];
  getSkillLevel(): string;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 PROJECT DETECTION ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

function detectProjectContext(projectType?: string, projectFiles?: string[]): ProjectContext {
  const files = projectFiles ?? [];
  const fileLower = files.map(f => f.toLowerCase());

  const hasPackageJson = fileLower.some(f => f === 'package.json' || f.endsWith('/package.json'));
  const hasCargoToml = fileLower.some(f => f === 'cargo.toml' || f.endsWith('/cargo.toml'));
  const hasGoMod = fileLower.some(f => f === 'go.mod' || f.endsWith('/go.mod'));
  const hasMakefile = fileLower.some(f => f === 'makefile' || f.endsWith('/makefile'));
  const hasDockerfile = fileLower.some(f => f === 'dockerfile' || f.endsWith('/dockerfile'));
  const hasGit = fileLower.some(f => f === '.git' || f.endsWith('/.git'));

  const detectedLanguages: string[] = [];
  const detectedFrameworks: string[] = [];

  // Language detection
  if (files.some(f => /\.tsx?$/.test(f))) detectedLanguages.push('typescript');
  if (files.some(f => /\.jsx?$/.test(f)) && !detectedLanguages.includes('typescript')) detectedLanguages.push('javascript');
  if (hasCargoToml) detectedLanguages.push('rust');
  if (hasGoMod) detectedLanguages.push('go');
  if (files.some(f => /\.py$/.test(f))) detectedLanguages.push('python');
  if (files.some(f => /\.rb$/.test(f))) detectedLanguages.push('ruby');
  if (files.some(f => /\.java$/.test(f))) detectedLanguages.push('java');
  if (files.some(f => /\.swift$/.test(f))) detectedLanguages.push('swift');

  // Framework detection
  if (fileLower.some(f => f.includes('svelte.config'))) detectedFrameworks.push('svelte');
  if (fileLower.some(f => f.includes('next.config'))) detectedFrameworks.push('next');
  if (fileLower.some(f => f.includes('nuxt.config'))) detectedFrameworks.push('nuxt');
  if (fileLower.some(f => f.includes('vite.config'))) detectedFrameworks.push('vite');
  if (fileLower.some(f => f.includes('webpack.config'))) detectedFrameworks.push('webpack');
  if (fileLower.some(f => f.includes('tailwind.config'))) detectedFrameworks.push('tailwind');
  if (fileLower.some(f => f.includes('docker-compose'))) detectedFrameworks.push('docker-compose');

  const type = projectType ?? (detectedLanguages[0] ?? 'unknown');

  return {
    type,
    files,
    detectedFrameworks,
    detectedLanguages,
    hasPackageJson,
    hasCargoToml,
    hasGoMod,
    hasMakefile,
    hasDockerfile,
    hasGit,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 CONTEXT-AWARE SUGGESTION ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

function suggestForProjectContext(ctx: ProjectContext, history: string[]): string[] {
  const suggestions: string[] = [];

  // Project-type-specific
  if (ctx.hasPackageJson) {
    suggestions.push('npm run dev', 'npm test', 'npm run build', 'npm install');
    if (ctx.detectedFrameworks.includes('vite')) suggestions.push('npx vite --open');
    if (ctx.detectedFrameworks.includes('next')) suggestions.push('npx next dev');
  }

  if (ctx.hasCargoToml) {
    suggestions.push('cargo build', 'cargo test', 'cargo run', 'cargo check', 'cargo clippy');
  }

  if (ctx.hasGoMod) {
    suggestions.push('go build ./...', 'go test ./...', 'go run .', 'go vet ./...');
  }

  if (ctx.detectedLanguages.includes('python')) {
    suggestions.push('python -m pytest', 'pip install -r requirements.txt', 'python main.py');
  }

  if (ctx.hasDockerfile) {
    suggestions.push('docker build -t app .', 'docker compose up', 'docker compose down');
  }

  if (ctx.hasGit) {
    suggestions.push('git status', 'git log --oneline -10', 'git diff', 'git pull');
  }

  if (ctx.hasMakefile) {
    suggestions.push('make', 'make test', 'make clean');
  }

  // Frequency-boosted: push recently used commands to the front
  const recentSet = new Set(history.slice(-20));
  const boosted = suggestions.filter(s => recentSet.has(s));
  const remaining = suggestions.filter(s => !recentSet.has(s));

  return [...boosted, ...remaining].slice(0, 15);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 ERROR ANALYSIS ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

interface ErrorPattern {
  readonly pattern: RegExp;
  readonly explanation: string;
  readonly suggestion: string;
}

const ERROR_PATTERNS: ErrorPattern[] = [
  { pattern: /command not found/i, explanation: 'The command is not installed or not in your PATH', suggestion: 'Install the package or check your PATH configuration' },
  { pattern: /permission denied/i, explanation: 'You lack the required permissions to perform this action', suggestion: 'Try with sudo (if appropriate) or check file permissions with ls -la' },
  { pattern: /ENOENT|no such file or directory/i, explanation: 'The specified file or directory does not exist', suggestion: 'Check the path with ls and verify the file exists' },
  { pattern: /EACCES/i, explanation: 'Access was denied to the file or resource', suggestion: 'Check file permissions — you may need to use chmod or run as a different user' },
  { pattern: /EADDRINUSE|address already in use/i, explanation: 'Another process is already using this port', suggestion: 'Find the process with lsof -i :PORT and kill it, or use a different port' },
  { pattern: /MODULE_NOT_FOUND|Cannot find module/i, explanation: 'A required dependency is missing', suggestion: 'Run npm install (or your package manager) to install dependencies' },
  { pattern: /SyntaxError/i, explanation: 'There is a syntax error in your code or command', suggestion: 'Check the file at the line number mentioned for typos or missing brackets' },
  { pattern: /TypeError/i, explanation: 'A value was used in an unexpected type context', suggestion: 'Check that variables are the expected type — look for null/undefined access' },
  { pattern: /ERR_DLOPEN_FAILED|native module/i, explanation: 'A native addon failed to load — likely a platform mismatch', suggestion: 'Try deleting node_modules and reinstalling: rm -rf node_modules && npm install' },
  { pattern: /ENOMEM|out of memory/i, explanation: 'The process ran out of available memory', suggestion: 'Increase memory limit with --max-old-space-size or reduce workload' },
  { pattern: /ECONNREFUSED/i, explanation: 'Connection was refused — the target service is not running', suggestion: 'Verify the service is running and listening on the expected host:port' },
  { pattern: /ETIMEOUT|timeout/i, explanation: 'The operation timed out waiting for a response', suggestion: 'Check network connectivity or increase the timeout setting' },
  { pattern: /git.*conflict/i, explanation: 'There are merge conflicts that need to be resolved', suggestion: 'Open conflicting files, resolve markers (<<<<<<<), then git add and commit' },
  { pattern: /npm ERR! peer dep/i, explanation: 'Peer dependency version conflicts detected', suggestion: 'Try npm install --legacy-peer-deps or update conflicting packages' },
  { pattern: /cargo.*error\[E/i, explanation: 'Rust compilation error', suggestion: 'Read the compiler message carefully — Rust errors are usually precise and helpful' },
];

function analyzeError(command: string, error: string): { explanation: string; suggestion: string } {
  for (const ep of ERROR_PATTERNS) {
    if (ep.pattern.test(error)) {
      return { explanation: ep.explanation, suggestion: ep.suggestion };
    }
  }

  // Fallback: generic analysis
  const hasLineNumber = /line\s+\d+|:\d+:\d+/.test(error);
  if (hasLineNumber) {
    return {
      explanation: 'An error occurred at a specific location in your code',
      suggestion: 'Check the file and line number mentioned in the error output',
    };
  }

  return {
    explanation: `The command "${command}" failed with an unrecognized error`,
    suggestion: 'Try running the command with verbose/debug flags for more detail',
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PROJECT ACTIONS GENERATOR
// ═══════════════════════════════════════════════════════════════════════════════

function generateProjectActions(ctx: ProjectContext): QuickAction[] {
  const actions: QuickAction[] = [];

  if (ctx.hasPackageJson) {
    actions.push(
      { id: 'npm-install', label: 'Install Dependencies', description: 'Install project dependencies', icon: 'package', category: 'dependencies', command: 'npm install', riskLevel: 'safe', requiresConfirmation: false, tags: ['npm', 'install'] },
      { id: 'npm-dev', label: 'Start Dev Server', description: 'Run development server', icon: 'play', category: 'project', command: 'npm run dev', riskLevel: 'safe', requiresConfirmation: false, tags: ['dev', 'server'] },
      { id: 'npm-test', label: 'Run Tests', description: 'Execute test suite', icon: 'test', category: 'project', command: 'npm test', riskLevel: 'safe', requiresConfirmation: false, tags: ['test'] },
      { id: 'npm-build', label: 'Build Project', description: 'Create production build', icon: 'build', category: 'project', command: 'npm run build', riskLevel: 'safe', requiresConfirmation: false, tags: ['build'] },
    );
  }

  if (ctx.hasCargoToml) {
    actions.push(
      { id: 'cargo-build', label: 'Cargo Build', description: 'Compile Rust project', icon: 'cog', category: 'project', command: 'cargo build', riskLevel: 'safe', requiresConfirmation: false, tags: ['rust', 'build'] },
      { id: 'cargo-test', label: 'Cargo Test', description: 'Run Rust tests', icon: 'test', category: 'project', command: 'cargo test', riskLevel: 'safe', requiresConfirmation: false, tags: ['rust', 'test'] },
      { id: 'cargo-clippy', label: 'Cargo Clippy', description: 'Run Rust linter', icon: 'search', category: 'project', command: 'cargo clippy', riskLevel: 'safe', requiresConfirmation: false, tags: ['rust', 'lint'] },
    );
  }

  if (ctx.hasGoMod) {
    actions.push(
      { id: 'go-build', label: 'Go Build', description: 'Build Go project', icon: 'cog', category: 'project', command: 'go build ./...', riskLevel: 'safe', requiresConfirmation: false, tags: ['go', 'build'] },
      { id: 'go-test', label: 'Go Test', description: 'Run Go tests', icon: 'test', category: 'project', command: 'go test ./...', riskLevel: 'safe', requiresConfirmation: false, tags: ['go', 'test'] },
    );
  }

  if (ctx.hasDockerfile) {
    actions.push(
      { id: 'docker-build', label: 'Docker Build', description: 'Build container image', icon: 'container', category: 'docker', command: 'docker build -t app .', riskLevel: 'moderate', requiresConfirmation: true, tags: ['docker', 'build'] },
      { id: 'docker-up', label: 'Docker Compose Up', description: 'Start all services', icon: 'play', category: 'docker', command: 'docker compose up -d', riskLevel: 'moderate', requiresConfirmation: false, tags: ['docker', 'compose'] },
    );
  }

  if (ctx.hasGit) {
    actions.push(
      { id: 'git-status', label: 'Git Status', description: 'View repository status', icon: 'git', category: 'git', command: 'git status', riskLevel: 'safe', requiresConfirmation: false, tags: ['git', 'status'] },
      { id: 'git-pull', label: 'Git Pull', description: 'Pull latest changes', icon: 'download', category: 'git', command: 'git pull', riskLevel: 'safe', requiresConfirmation: false, tags: ['git', 'pull'] },
    );
  }

  return actions;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 FACTORY — createIntelligentTerminal
// ═══════════════════════════════════════════════════════════════════════════════

export function createIntelligentTerminal(config: IntelligentTerminalConfig): IntelligentTerminalHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ── Initialize state ──
  const shell: ShellType = config.shell ?? 'zsh';
  const colorScheme = config.colorScheme ?? 'dark';
  const projectContext = detectProjectContext(config.projectType, config.projectFiles);

  const termResult = createTerminal({ shell });
  if (!termResult.success) throw new Error(`Terminal creation failed: ${termResult.code}`);
  let terminalState: TerminalState = termResult.value;

  const commandHistory: string[] = [];
  const uniqueCommands = new Set<string>();
  let skillLevel: SkillLevel = 'beginner';

  // ── Methods ──

  function getState(): TerminalState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return terminalState;
  }

  function suggestForContext(): string[] {
    guardDestroyed();
    const suggestions = suggestForProjectContext(projectContext, commandHistory);
    circuitStats = recordSuccess(circuitStats);
    return suggestions;
  }

  function doPreviewCommand(command: string): CommandPreview {
    guardDestroyed();
    try {
      const preview = generatePreview(command);
      circuitStats = recordSuccess(circuitStats);
      return preview;
    } catch {
      circuitStats = recordError(circuitStats);
      return {
        type: 'none',
        command,
        summary: 'Unable to generate preview',
        details: [],
        affectedPaths: [],
        riskLevel: 'safe',
        canDryRun: false,
      };
    }
  }

  function doDigestOutput(command: string, output: string, exitCode: number): OutputDigest {
    guardDestroyed();
    try {
      const digest = digestOutput(command, output, exitCode);
      circuitStats = recordSuccess(circuitStats);
      return digest;
    } catch {
      circuitStats = recordError(circuitStats);
      return {
        type: 'raw-output',
        headline: exitCode === 0 ? 'Command completed' : 'Command failed',
        status: exitCode === 0 ? 'success' : 'error',
        details: [],
        rawOutput: output,
        showRaw: true,
      };
    }
  }

  function doExplainError(command: string, error: string): { explanation: string; suggestion: string } {
    guardDestroyed();
    const result = analyzeError(command, error);
    circuitStats = recordSuccess(circuitStats);
    return result;
  }

  function getProjectActionsValue(): QuickAction[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return generateProjectActions(projectContext);
  }

  function doTranslateNL(input: string): { command: string; confidence: number } {
    guardDestroyed();
    try {
      const result = translateNaturalLanguage(input);
      circuitStats = recordSuccess(circuitStats);
      return { command: result.command, confidence: result.confidence };
    } catch {
      circuitStats = recordError(circuitStats);
      return { command: '', confidence: 0 };
    }
  }

  function doGetHint(command: string): CommandHint | null {
    guardDestroyed();
    try {
      const hint = generatePreExecutionHint(command);
      circuitStats = recordSuccess(circuitStats);
      return hint;
    } catch {
      circuitStats = recordError(circuitStats);
      return null;
    }
  }

  function doRecordCommand(command: string): void {
    guardDestroyed();
    commandHistory.push(command);
    if (commandHistory.length > 5000) commandHistory.shift();
    const base = command.split(' ')[0]?.toLowerCase() ?? '';
    if (base) uniqueCommands.add(base);

    // Reassess skill
    const unique = uniqueCommands.size;
    if (unique >= 40) skillLevel = 'expert';
    else if (unique >= 20) skillLevel = 'advanced';
    else if (unique >= 8) skillLevel = 'intermediate';
    else skillLevel = 'beginner';

    circuitStats = recordSuccess(circuitStats);
  }

  function getCommandHistoryValue(): string[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return commandHistory.slice(-100);
  }

  function getSkillLevelValue(): string {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return skillLevel;
  }

  function getCircuitStats(): CircuitStats {
    return { ...circuitStats, uptime: Date.now() - createdAt };
  }

  function getHealth(): CircuitHealthReport {
    const h = destroyed ? 'destroyed' as const : healthFromStats(circuitStats);
    return buildHealthReport('intelligent-terminal', h, formatStatsDetails(circuitStats), createdAt);
  }

  function destroy(): void {
    destroyed = true;
  }

  return Object.freeze({
    name: 'intelligent-terminal',
    getState,
    suggestForContext,
    previewCommand: doPreviewCommand,
    digestOutput: doDigestOutput,
    explainError: doExplainError,
    getProjectActions: getProjectActionsValue,
    translateNL: doTranslateNL,
    getHint: doGetHint,
    recordCommand: doRecordCommand,
    getCommandHistory: getCommandHistoryValue,
    getSkillLevel: getSkillLevelValue,
    stats: getCircuitStats,
    health: getHealth,
    destroy,
  });
}
